/* ════════════════════════════════════════════════════════════════════════════
   st-core.js — /speaker-test: решения прибора, отдельно от звука и разметки.

   Здесь только чистые функции: одно и то же правило читает страница и
   node-тест (tests/js/speaker-test-core.test.mjs). Всё, что говорит человеку
   «правый наушник тише на 4 дБ» или «звук пропал на 15,8 кГц», выводится
   отсюда, а не из блейда.

   ── Баланс: как отличить слабый наушник от слабого уха ──────────────────────
   Человек двигает ползунок, пока звук не встанет посередине головы. Ползунок
   меняет РАЗНИЦУ уровней каналов b = gR − gL (дБ). Посередине звук стоит, когда
   громкость в двух ушах равна:

       обычная посадка   gL + hL + eL = gR + hR + eR  →  b1 = H + E
       наушники наоборот gR + hR + eL = gL + hL + eR  →  b2 = H − E

   где H = hL − hR (насколько правый НАУШНИК тише левого), E = eL − eR
   (насколько правое УХО слышит тише). Отсюда H = (b1 + b2)/2, E = (b1 − b2)/2.
   Иначе говоря: если тише наушник, нужная поправка КАНАЛА после перестановки
   остаётся той же (канал едет вместе с наушником), если тише ухо — меняет знак.
   ⚠️ Интуиция «тихая сторона следует за наушником» верна про ощущение, но в
   единицах канала — наоборот; вывод выше проверен на двух примерах в тесте.

   Пороги — не вкус. Порог различения межушной разницы уровней у человека
   ≈ 0,5–1 дБ, а разброс повторной установки «на середину» — около ±1 дБ.
   Поэтому < 1 дБ — «одинаково», и компонента разложения считается настоящей
   только от 1,5 дБ (среднее двух установок, погрешность ≈ 0,7 дБ).
   ═══════════════════════════════════════════════════════════════════════════ */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.SpeakerTestCore = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var BAL_MAX = 12;          // ползунок баланса, ± дБ
    var SAME_DB = 1;           // меньше — на слух не отличить
    var SLIGHT_DB = 3;
    var STRONG_DB = 6;
    var SPLIT_DB = 1.5;        // компонента «наушник / ухо» считается от этого
    var EQUAL_POWER = Math.SQRT1_2;

    function num(v, fb) { v = +v; return isFinite(v) ? v : (fb === undefined ? 0 : fb); }
    function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
    function round1(v) { return Math.round(v * 10) / 10; }
    function dbToAmp(db) { return Math.pow(10, num(db) / 20); }

    /* Уровни каналов при разнице b: громче остаётся на единице, тише — ослаблен.
       Никогда не усиливаем выше единицы: громкость задаёт общий ползунок. */
    function balanceGains(b) {
        b = clamp(num(b), -BAL_MAX, BAL_MAX);
        return { l: b > 0 ? dbToAmp(-b) : 1, r: b < 0 ? dbToAmp(b) : 1 };
    }

    /* Что значит одна установка «посередине»: b > 0 — правый канал пришлось
       сделать громче, значит правая сторона тише. */
    function balanceReading(b) {
        b = num(b);
        var a = Math.abs(b);
        var grade = a < SAME_DB ? 'same' : a < SLIGHT_DB ? 'slight' : a < STRONG_DB ? 'noticeable' : 'strong';
        return { quieter: grade === 'same' ? 'none' : (b > 0 ? 'right' : 'left'), db: round1(a), grade: grade };
    }

    function part(x) {
        var a = Math.abs(x);
        return { quieter: a < SPLIT_DB ? 'none' : (x > 0 ? 'right' : 'left'), db: round1(a) };
    }

    /* Разложение двух установок (обычная посадка b1, наушники наоборот b2;
       обе — в единицах КАНАЛА) на вклад наушников и вклад ушей. */
    function swapVerdict(b1, b2) {
        b1 = num(b1); b2 = num(b2);
        var bud = part((b1 + b2) / 2), ear = part((b1 - b2) / 2);
        var kind = bud.quieter !== 'none' && ear.quieter !== 'none' ? 'both'
                 : bud.quieter !== 'none' ? 'bud'
                 : ear.quieter !== 'none' ? 'ear' : 'gone';
        return { bud: bud, ear: ear, kind: kind };
    }

    /* Стартовая точка ползунка — случайная, 4–7 дБ в случайную сторону: иначе
       «посередине» можно нажать, ничего не двигая, и получить «одинаково». */
    function randomStart(rnd) {
        rnd = rnd || Math.random;
        var mag = 4 + Math.round(rnd() * 6) / 2;
        return rnd() < 0.5 ? -mag : mag;
    }

    /* Каналы стерео-теста. «Оба» — равная мощность (−3 дБ на канал), как у
       StereoPanner в центре: громкость не прыгает при переходе L → оба → R.
       invert переворачивает правый канал — это и есть проверка фазы. */
    function pairGains(ch, invert) {
        if (ch === 'L') return { l: 1, r: 0 };
        if (ch === 'R') return { l: 0, r: 1 };
        return { l: EQUAL_POWER, r: invert ? -EQUAL_POWER : EQUAL_POWER };
    }

    /* Равномощная панорама p ∈ [−1, 1] (режим «вести мышью»). */
    function panGains(p) {
        p = clamp(num(p), -1, 1);
        var a = (p + 1) * Math.PI / 4;
        return { l: Math.cos(a), r: Math.sin(a) };
    }

    /* ── Свипы ─────────────────────────────────────────────────────────────
       Длительности медленнее прежних (5 с на диапазон): по такому свипу
       успеваешь услышать, где звук пропал или задребезжал, и нажать.
       mark: onset — «начал слышать» (бас снизу), offset — «перестал слышать»
       (высокие вверх), note — просто отметка (провал, дребезг). */
    var SWEEPS = {
        full: { f0: 20, f1: 20000, dur: 16, mark: 'note' },
        bass: { f0: 20, f1: 250, dur: 10, mark: 'onset' },
        mid: { f0: 250, f1: 4000, dur: 8, mark: 'note' },
        high: { f0: 4000, f1: 20000, dur: 10, mark: 'offset' },
        rattle: { f0: 40, f1: 500, dur: 14, mark: 'note' }
    };

    /* Экспоненциальный свип — тот же закон, что exponentialRampToValueAtTime. */
    function sweepFreqAt(f0, f1, dur, t) {
        f0 = num(f0, 20); f1 = num(f1, 20000); dur = Math.max(0.001, num(dur, 1)); t = num(t);
        if (t <= 0) return f0;
        if (t >= dur) return f1;
        return f0 * Math.pow(f1 / f0, t / dur);
    }

    /* Время реакции на появление/исчезновение звука — проектная константа, а не
       замер этого человека: простая реакция на звук ≈ 150–200 мс, на исчезновение
       дольше. Задержку УСТРОЙСТВА отдельно вычитает AudibleClock (у Bluetooth
       150–300 мс) — смешивать их нельзя, иначе не узнать, какая из двух врёт. */
    var REACTION_S = 0.2;

    /* heardS — время графа, которое человек СЛЫШАЛ в момент нажатия
       (AudibleClock.audible); startS — время графа начала свипа. */
    function markFrequency(sweep, startS, heardS) {
        var t = num(heardS) - num(startS) - REACTION_S;
        return sweepFreqAt(sweep.f0, sweep.f1, sweep.dur, t);
    }

    /* Частота для чтения: «440 Гц», «31,5 Гц», «15,8 кГц». Числа — из Intl той
       локали, что дал тул (у ar — латинские цифры, см. __intlLocale). */
    function fmtFreq(hz, locale) {
        hz = num(hz);
        var k = hz >= 1000, v = k ? hz / 1000 : hz;
        var digits = k ? (v < 10 ? 2 : 1) : (v < 100 ? 1 : 0);
        var s;
        try { s = new Intl.NumberFormat(locale || 'en', { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(v); }
        catch (e) { s = String(Math.round(v * Math.pow(10, digits)) / Math.pow(10, digits)); }
        return { v: s, k: k };
    }

    /* ── Микрофон слушает каждый канал ────────────────────────────────────
       res — ответы SpeakerProbe.run на левый и правый канал по отдельности. */
    function micVerdict(resL, resR) {
        var hl = !!(resL && resL.heard), hr = !!(resR && resR.heard);
        if (hl && hr) return 'both';
        if (hl) return 'left';
        if (hr) return 'right';
        var silent = (resL && resL.failure === 'silent') && (resR && resR.failure === 'silent');
        return silent ? 'mic_silent' : 'none';
    }

    /* ── Быстрая проверка: левый → правый → оба ──────────────────────────
       Голосом шаг длится сколько звучит слово плюс пауза; тоном — 1,2 с. */
    function quickPlan(clipDur) {
        var d = { L: num(clipDur && clipDur.L, 0), R: num(clipDur && clipDur.R, 0), C: num(clipDur && clipDur.C, 0) };
        return ['L', 'R', 'C'].map(function (ch) {
            return { ch: ch, dur: d[ch] > 0 ? d[ch] + 0.55 : 1.2 };
        });
    }

    return {
        BAL_MAX: BAL_MAX, SAME_DB: SAME_DB, SPLIT_DB: SPLIT_DB, REACTION_S: REACTION_S, SWEEPS: SWEEPS,
        dbToAmp: dbToAmp, clamp: clamp,
        balanceGains: balanceGains, balanceReading: balanceReading, swapVerdict: swapVerdict,
        randomStart: randomStart, pairGains: pairGains, panGains: panGains,
        sweepFreqAt: sweepFreqAt, markFrequency: markFrequency, fmtFreq: fmtFreq,
        micVerdict: micVerdict, quickPlan: quickPlan
    };
});
